// Copyright (c) Mysten Labs, Inc.
// Modifications Copyright (c) 2024 IOTA Stiftung
// SPDX-License-Identifier: Apache-2.0

import {
    getTotalGasUsed,
    getTransactionAction,
    getTransactionCoinBalances,
    TransactionIcon,
    TransactionIconSize,
} from '@iota/core';
import type { IotaTransactionBlockKind, IotaTransactionBlockResponse } from '@iota/iota-sdk/client';

import { TableCellBase, TableCellText, Tooltip, ROW_LINK_PROPS } from '@iota/apps-ui-kit';
import type { ColumnDef } from '@tanstack/react-table';
import { AddressLink, ObjectLink, TransactionLink } from '../../../components/ui';
import { CoinFormat, formatBalance, formatDigest, IOTA_DECIMALS } from '@iota/iota-sdk/utils';
import { TableCoinChanges, DateDisplay } from '~/components';
import { getLastMoveCall, getTransactionTypeLabel } from './generateTransactionsTableColumns';

function getCounterpartyAddress(
    txn: IotaTransactionBlockResponse,
    address: string,
): string | undefined {
    const sender = txn.transaction?.data.sender;
    if (sender && sender !== address) {
        return sender;
    }
    const recipient = txn.balanceChanges?.find(
        (change) =>
            change.owner &&
            typeof change.owner === 'object' &&
            'AddressOwner' in change.owner &&
            change.owner.AddressOwner !== address &&
            Number(change.amount) > 0,
    );
    return recipient && typeof recipient.owner === 'object' && 'AddressOwner' in recipient.owner
        ? recipient.owner.AddressOwner
        : undefined;
}

/**
 * Generate table columns renderers for the "Activity" view of an address's transactions:
 * a more digestible, human-friendly presentation than the raw "Transaction Blocks" table.
 */
export function generateActivityTableColumns(
    address: string,
): ColumnDef<IotaTransactionBlockResponse>[] {
    return [
        {
            header: 'Type',
            accessorKey: 'Type',
            cell: ({ row }) => {
                const txn = row.original;
                const digest = txn.digest;
                const isSuccess = txn.effects?.status.status === 'success';
                const action = getTransactionAction(txn, address);
                const typeLabel = getTransactionTypeLabel(txn, action, isSuccess);
                return (
                    <TableCellBase>
                        <TransactionLink
                            {...ROW_LINK_PROPS}
                            digest={digest}
                            copyText={digest}
                            label={
                                <div className="flex items-center gap-xs">
                                    <TransactionIcon
                                        variant={action}
                                        txnFailed={!isSuccess}
                                        size={TransactionIconSize.Small}
                                    />
                                    <div className="flex flex-col">
                                        <span
                                            className="block max-w-[110px] truncate text-label-lg text-iota-neutral-40 dark:text-iota-neutral-60"
                                            title={typeLabel}
                                        >
                                            {typeLabel}
                                        </span>
                                        <span className="text-body-sm text-iota-primary-30 dark:text-iota-primary-80">
                                            {formatDigest(digest)}
                                        </span>
                                    </div>
                                </div>
                            }
                        />
                    </TableCellBase>
                );
            },
        },
        {
            header: 'Function',
            accessorKey: 'function',
            cell: ({ row }) => {
                const kind = row.original.transaction?.data.transaction.kind;
                const kindLabel = kind === 'ProgrammableTransaction' ? 'Programmable Tx' : '--';
                const moveCall = getLastMoveCall(row.original);
                return (
                    <TableCellBase>
                        <div className="flex flex-col">
                            <span className="text-label-lg text-iota-neutral-40 dark:text-iota-neutral-60">
                                {kindLabel}
                            </span>
                            {moveCall && (
                                <ObjectLink
                                    objectId={`${moveCall.package}?module=${moveCall.module}`}
                                    copyText={moveCall.package}
                                    showAddressAlias={false}
                                    label={moveCall.function}
                                    className="text-body-sm"
                                />
                            )}
                        </div>
                    </TableCellBase>
                );
            },
        },
        {
            header: 'Sender',
            accessorKey: 'transaction.data.sender',
            meta: { tooltip: 'The address that signed and submitted this transaction.' },
            cell: ({ getValue }) => {
                const sender = getValue<string>();
                return (
                    <TableCellBase>
                        <AddressLink
                            address={sender}
                            copyText={sender}
                            className="[&>div]:max-w-[160px] [&>div]:truncate"
                        />
                    </TableCellBase>
                );
            },
        },
        {
            header: 'With',
            accessorKey: 'with',
            meta: {
                tooltip: 'Who this address sent to, or received from.',
            },
            cell: ({ row }) => {
                const counterparty = getCounterpartyAddress(row.original, address);
                if (!counterparty) {
                    return (
                        <TableCellBase>
                            <TableCellText>--</TableCellText>
                        </TableCellBase>
                    );
                }
                return (
                    <TableCellBase>
                        <AddressLink
                            address={counterparty}
                            copyText={counterparty}
                            className="[&>div]:max-w-[160px] [&>div]:truncate"
                        />
                    </TableCellBase>
                );
            },
        },
        {
            header: 'Balance Change',
            accessorKey: 'balanceChanges',
            cell: ({ row }) => {
                const txn = row.original;
                const perOwnerChanges = getTransactionCoinBalances(txn)?.owners;
                const balanceChanges = perOwnerChanges?.find(
                    ({ owner }) => owner === address,
                )?.changes;

                if (!balanceChanges) {
                    return (
                        <TableCellBase>
                            <TableCellText>--</TableCellText>
                        </TableCellBase>
                    );
                }

                return (
                    <TableCellBase>
                        <TableCoinChanges changes={balanceChanges} />
                    </TableCellBase>
                );
            },
        },
        {
            header: 'Gas Fee',
            accessorKey: 'effects',
            cell: ({ getValue }) => {
                const effects = getValue<IotaTransactionBlockResponse['effects']>();
                const totalGasUsed = effects ? getTotalGasUsed(effects)?.toString() : undefined;
                const totalGasUsedFormatted = totalGasUsed
                    ? formatBalance(totalGasUsed, IOTA_DECIMALS, CoinFormat.Full)
                    : '--';
                return (
                    <TableCellBase>
                        <div className="flex flex-row items-center gap-1">
                            {totalGasUsed ? (
                                <Tooltip text={`${totalGasUsedFormatted} IOTA`}>
                                    <span className="block max-w-[80px] truncate">
                                        {totalGasUsedFormatted}
                                    </span>
                                </Tooltip>
                            ) : (
                                <span>{totalGasUsedFormatted}</span>
                            )}

                            {totalGasUsed && (
                                <span className="table-cell-supporting-label-color text-body-sm">
                                    IOTA
                                </span>
                            )}
                        </div>
                    </TableCellBase>
                );
            },
        },
        {
            header: 'Txns',
            accessorKey: 'transaction.data.transaction',
            cell: ({ getValue }) => {
                const transaction = getValue<IotaTransactionBlockKind>();
                const txns =
                    transaction.kind === 'ProgrammableTransaction'
                        ? transaction.transactions.length.toString()
                        : '--';
                return (
                    <TableCellBase>
                        <TableCellText>{txns}</TableCellText>
                    </TableCellBase>
                );
            },
        },
        {
            header: 'Time',
            accessorKey: 'timestampMs',
            cell: ({ getValue }) => {
                const timestampMs = getValue();
                return (
                    <TableCellBase>
                        <TableCellText>
                            {timestampMs ? (
                                <DateDisplay timestamp={Number(timestampMs)} type="table" />
                            ) : (
                                '--'
                            )}
                        </TableCellText>
                    </TableCellBase>
                );
            },
        },
    ];
}
